import { defineStore } from 'pinia'
import { ref } from 'vue'
import { showService } from '@/services/showService'
import { logger } from '@/services/loggerService'

export const useClientShowsStore = defineStore('clientShows', () => {
  // ─── State ───────────────────────────────────────
  const shows = ref(new Map())
  const currentShow = ref(null)
  const isLoading = ref(false)
  let unsubscribeCompanyShows = null
  let unsubscribeCurrentShow = null
  // Track which IDs we're currently subscribed to so repeated calls
  // (e.g. five navbar components each calling subscribeToCompanyShows on
  // mount) become no-ops instead of detaching/re-attaching listeners.
  let subscribedCompanyId = null
  let subscribedShowId = null

  // ─── Actions ─────────────────────────────────────

  /**
   * Subscribe to all shows for a company (real-time)
   */
  function subscribeToCompanyShows(companyId) {
    // Idempotent: if already subscribed to this company, no-op
    if (unsubscribeCompanyShows && subscribedCompanyId === companyId) {
      return
    }
    // Clean up previous listener
    if (unsubscribeCompanyShows) {
      unsubscribeCompanyShows()
      unsubscribeCompanyShows = null
    }

    subscribedCompanyId = companyId
    isLoading.value = true

    unsubscribeCompanyShows = showService.subscribeToCompanyShows(
      companyId,
      (showsList) => {
        shows.value = new Map(showsList.map(s => [s.id, s]))
        isLoading.value = false

        logger.info('[ClientShowsStore] Company shows updated', {
          companyId,
          count: showsList.length
        })
      },
      (error) => {
        isLoading.value = false
        logger.firestoreListenerError('[ClientShowsStore] Company shows subscription failed', error, { companyId })
      }
    )
  }

  /**
   * Subscribe to a single show by ID (real-time)
   */
  function subscribeToShow(showId) {
    // Idempotent: if already subscribed to this show, no-op
    if (unsubscribeCurrentShow && subscribedShowId === showId) {
      return
    }
    // Clean up previous listener
    if (unsubscribeCurrentShow) {
      unsubscribeCurrentShow()
      unsubscribeCurrentShow = null
    }

    subscribedShowId = showId
    isLoading.value = true

    unsubscribeCurrentShow = showService.subscribeToShow(
      showId,
      (show) => {
        if (show) {
          shows.value.set(show.id, show)
          currentShow.value = show
        } else {
          currentShow.value = null
        }
        isLoading.value = false

        logger.info('[ClientShowsStore] Show updated', { showId, found: !!show })
      },
      (error) => {
        isLoading.value = false
        logger.error('[ClientShowsStore] Show subscription failed', {
          showId,
          error: error.message
        })
      }
    )
  }

  /**
   * Update editable show details (name, description, logo, platform links).
   * Firestore rules restrict clients to presentation fields only.
   */
  async function updateShow(showId, updates) {
    try {
      await showService.updateShow(showId, updates)
      logger.info('[ClientShowsStore] Show updated', { showId, keys: Object.keys(updates) })
    } catch (error) {
      logger.error('[ClientShowsStore] Failed to update show', {
        error: error.message, showId
      })
      throw error
    }
  }

  /**
   * Update a setup checklist item
   */
  async function updateSetupChecklist(showId, stepKey, completed) {
    try {
      await showService.updateSetupChecklistItem(showId, stepKey, completed)
      logger.info('[ClientShowsStore] Setup checklist updated', { showId, stepKey, completed })
    } catch (error) {
      logger.error('[ClientShowsStore] Failed to update setup checklist', {
        error: error.message, showId, stepKey
      })
      throw error
    }
  }

  function getShowById(showId) {
    return shows.value.get(showId) || null
  }

  // Scoped disposers — each consumer disposes only what it created.
  // The shared `dispose()` would otherwise kill subscriptions belonging to
  // other components when ClientShows auto-redirects to ClientShowDetail.
  function disposeCompanyShows() {
    if (unsubscribeCompanyShows) {
      unsubscribeCompanyShows()
      unsubscribeCompanyShows = null
    }
    subscribedCompanyId = null
  }

  function disposeCurrentShow() {
    if (unsubscribeCurrentShow) {
      unsubscribeCurrentShow()
      unsubscribeCurrentShow = null
    }
    subscribedShowId = null
  }

  function dispose() {
    disposeCompanyShows()
    disposeCurrentShow()
    isLoading.value = false
  }

  function reset() {
    dispose()
    shows.value = new Map()
    currentShow.value = null
  }

  return {
    // State
    shows,
    currentShow,
    isLoading,
    // Actions
    subscribeToCompanyShows,
    subscribeToShow,
    updateShow,
    updateSetupChecklist,
    getShowById,
    disposeCompanyShows,
    disposeCurrentShow,
    dispose,
    reset
  }
})
